// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon, IconButton, OptionsSelector, Table } from '@shared/ui-kit';
import { DateInput, Input, Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  CapitalCallSubtype,
  DistributionSubtype,
  IlliquidAssetEventType,
  type IlliquidAssetCurrency,
  type IlliquidAssetEvent,
} from '@shared/data-access/types/engine-types';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon';
import {
  Button,
  CircularProgress,
  Collapse,
  Tooltip,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import type { Path } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import type { IlliquidEventFormData } from '../../types/illiquid-event-form';
import {
  useIlliquidAssetFinancialEventsSection,
  type IlliquidAssetFinancialEventsSectionProps,
} from './use-illiquid-asset-financial-events-section';
import styles from './illiquid-asset-financial-events-section.module.css';

/**
 * Financial events timeline and create form for an illiquid asset.
 *
 * @remarks
 * `event_date` is date-only (`YYYY-MM-DD`). The value-evolution chart builds series
 * points from each valuation event's `created_at` (`YYYY-MM-DDTHH:mm:ssZ`) so
 * same-day valuations do not collide.
 */

export type { IlliquidAssetFinancialEventsSectionProps };

type EventVisualTone = 'neutral' | 'positive' | 'warning';

type EventVisual = {
  readonly icon: SvgIconName;
  readonly tone: EventVisualTone;
  readonly showPositiveAmount: boolean;
};

const EVENT_LABEL_KEY_BY_TYPE: Record<IlliquidAssetEventType, string> = {
  [IlliquidAssetEventType.Acquisition]:
    'illiquid-asset-financial-events.event-type.acquisition',
  [IlliquidAssetEventType.Valuation]:
    'illiquid-asset-financial-events.event-type.valuation',
  [IlliquidAssetEventType.CapitalCall]:
    'illiquid-asset-financial-events.event-type.capital-call',
  [IlliquidAssetEventType.Distribution]:
    'illiquid-asset-financial-events.event-type.distribution',
};

const CAPITAL_CALL_SUBTYPE_LABEL_KEY: Record<CapitalCallSubtype, string> = {
  [CapitalCallSubtype.Executed]:
    'illiquid-asset-financial-events.subtype.capital-call.executed',
  [CapitalCallSubtype.Planned]:
    'illiquid-asset-financial-events.subtype.capital-call.planned',
};

const DISTRIBUTION_SUBTYPE_LABEL_KEY: Record<DistributionSubtype, string> = {
  [DistributionSubtype.Liquidity]:
    'illiquid-asset-financial-events.subtype.distribution.liquidity',
  [DistributionSubtype.PartialSale]:
    'illiquid-asset-financial-events.subtype.distribution.partial-sale',
  [DistributionSubtype.FullSale]:
    'illiquid-asset-financial-events.subtype.distribution.full-sale',
  [DistributionSubtype.Dividend]:
    'illiquid-asset-financial-events.subtype.distribution.dividend',
};

const EVENT_ICON_WRAP_BY_TONE: Record<EventVisualTone, string> = {
  neutral: styles['event-icon-wrap-neutral'],
  positive: styles['event-icon-wrap-positive'],
  warning: styles['event-icon-wrap-warning'],
};

const getEventVisual = (event: IlliquidAssetEvent): EventVisual => {
  switch (event.event_type) {
    case IlliquidAssetEventType.CapitalCall:
      return {
        icon: 'TrendingUp',
        tone: 'warning',
        showPositiveAmount: false,
      };

    case IlliquidAssetEventType.Valuation:
      return {
        icon: 'Settings',
        tone: 'neutral',
        showPositiveAmount: false,
      };

    case IlliquidAssetEventType.Acquisition:
      return {
        icon: 'ShoppingCart',
        tone: 'neutral',
        showPositiveAmount: false,
      };

    case IlliquidAssetEventType.Distribution:
      switch (event.subtype) {
        case DistributionSubtype.PartialSale:
        case DistributionSubtype.FullSale:
          return {
            icon: 'Banknote',
            tone: 'positive',
            showPositiveAmount: true,
          };

        case DistributionSubtype.Dividend:
          return {
            icon: 'DollarSign',
            tone: 'positive',
            showPositiveAmount: true,
          };

        case DistributionSubtype.Liquidity:
        default:
          return {
            icon: 'TrendingDown',
            tone: 'positive',
            showPositiveAmount: true,
          };
      }
  }
};

const getSubtypeLabelKey = (event: IlliquidAssetEvent): string | null => {
  if (event.event_type === IlliquidAssetEventType.CapitalCall && event.subtype) {
    return CAPITAL_CALL_SUBTYPE_LABEL_KEY[event.subtype];
  }

  if (event.event_type === IlliquidAssetEventType.Distribution && event.subtype) {
    return DISTRIBUTION_SUBTYPE_LABEL_KEY[event.subtype];
  }

  return null;
};

const EventRow = ({
  event,
  displayCurrency,
  deleteLabel,
  isDeleting,
  onDelete,
}: {
  readonly event: IlliquidAssetEvent;
  readonly displayCurrency: IlliquidAssetCurrency | string;
  readonly deleteLabel: string;
  readonly isDeleting: boolean;
  readonly onDelete: (event: IlliquidAssetEvent) => void;
}) => {
  const { icon, tone, showPositiveAmount } = getEventVisual(event);

  const amountCurrency = event.currency ?? displayCurrency;

  const amountValue = event.amount;

  const subtypeLabelKey = getSubtypeLabelKey(event);

  return (
    <li className={styles['event-row']} data-testid={`financial-event-${event.id}`}>
      <span
        className={classNames(
          styles['event-icon-wrap'],
          EVENT_ICON_WRAP_BY_TONE[tone],
        )}
      >
        <AppIcon icon={icon} size="base" color="inherit" />
      </span>

      <div className={styles['event-content']}>
        <Typography component="p" className={styles['event-title']}>
          <Translation
            i18nKey={EVENT_LABEL_KEY_BY_TYPE[event.event_type] as any}
            ns="components"
          />
        </Typography>

        <Typography component="p" className={styles['event-meta']}>
          {subtypeLabelKey ? (
            <>
              <Translation i18nKey={subtypeLabelKey as any} ns="components" />
              {' · '}
            </>
          ) : null}
          <Table.Data
            value={event.event_date}
            format="date"
            formatOptions={{ day: 'numeric', month: 'short', year: 'numeric' }}
            safeValue="-"
          />
        </Typography>

        {event.description ? (
          <Typography
            component="p"
            className={styles['event-detail']}
            title={event.description}
          >
            {event.description}
          </Typography>
        ) : null}
      </div>

      <div className={styles['event-trailing']}>
        <Typography
          component="p"
          className={classNames(
            styles['event-amount'],
            'money-display',
            showPositiveAmount ? styles['event-amount-positive'] : undefined,
          )}
        >
          {showPositiveAmount ? '+' : null}
          <Table.Data
            value={amountValue}
            format="currency"
            currency={amountCurrency}
            safeValue="-"
            emptyValue={null}
          />
        </Typography>

        <Tooltip title={deleteLabel}>
          <span>
            <IconButton
              icon="Trashcan"
              variant="icon"
              color="dark"
              size="lg"
              aria-label={deleteLabel}
              data-testid={`financial-event-delete-${event.id}`}
              disabled={isDeleting}
              onClick={() => onDelete(event)}
            />
          </span>
        </Tooltip>
      </div>
    </li>
  );
};

export const IlliquidAssetFinancialEventsSection = defineComponent({
  displayName: 'IlliquidAssetFinancialEventsSection',
  useSetup: useIlliquidAssetFinancialEventsSection,
  View: ({
    t,
    form,
    showForm,
    onToggleForm,
    onCancelForm,
    onSubmit,
    onDeleteEvent,
    onEventTypeChange,
    selectedEventType,
    selectedSubtype,
    groupedEvents,
    hasEvents,
    isSaving,
    deletingEventId,
    currencyOptions,
    eventTypeOptions,
    capitalCallSubtypeOptions,
    distributionSubtypeOptions,
    defaultCurrency,
    displayCurrency,
  }) => {
    const deleteLabel = t('illiquid-asset-financial-events.delete-event', {
      ns: 'components',
    });

    const showSubtypeField =
      selectedEventType === IlliquidAssetEventType.CapitalCall ||
      selectedEventType === IlliquidAssetEventType.Distribution;

    const subtypeOptions =
      selectedEventType === IlliquidAssetEventType.CapitalCall
        ? capitalCallSubtypeOptions
        : distributionSubtypeOptions;

    const selectedEventTypeLabel =
      eventTypeOptions.find(option => option.value === selectedEventType)?.label ??
      '';

    const selectedSubtypeLabel =
      subtypeOptions.find(option => option.value === selectedSubtype)?.label ?? '';

    return (
      <section
        className={styles.section}
        data-testid="illiquid-asset-financial-events-section"
      >
        <header className={styles['section-header']}>
          <Typography component="h3" className={styles['section-title']}>
            <Translation
              i18nKey="illiquid-asset-financial-events.title"
              ns="components"
            />
          </Typography>

          <Button
            type="button"
            variant="outlined"
            color="secondary"
            className={styles['add-event-button']}
            onClick={onToggleForm}
            startIcon={<AppIcon icon="Plus" color="inherit" size="base" />}
            data-testid="illiquid-asset-financial-events-add"
          >
            <Translation
              i18nKey="illiquid-asset-financial-events.add-event"
              ns="components"
            />
          </Button>
        </header>

        <Collapse in={showForm} unmountOnExit>
          <form
            className={styles['form-stack']}
            onSubmit={onSubmit}
            data-testid="illiquid-asset-financial-events-form"
          >
            <div className={styles['field-stack']}>
              <Label htmlFor="illiquid-event-type" required>
                <Translation
                  i18nKey="illiquid-asset-financial-events.fields.event-type"
                  ns="components"
                />
              </Label>
              <OptionsSelector
                data-testid="illiquid-event-type"
                optionsAvailable={eventTypeOptions}
                onChangeValueSelected={value =>
                  onEventTypeChange(value as IlliquidAssetEventType)
                }
                optionSelected={selectedEventType}
                defaultText={selectedEventTypeLabel}
                defaultValue={selectedEventType}
                selectType="white"
                required
              />
            </div>

            {showSubtypeField ? (
              <Controller
                control={form.control}
                name={'subtype' as Path<IlliquidEventFormData>}
                render={({ field, fieldState }) => (
                  <div className={styles['field-stack']}>
                    <Label
                      htmlFor="illiquid-event-subtype"
                      required
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="illiquid-asset-financial-events.fields.subtype"
                        ns="components"
                      />
                    </Label>
                    <OptionsSelector
                      data-testid="illiquid-event-subtype"
                      optionsAvailable={subtypeOptions}
                      onChangeValueSelected={val => field.onChange(val)}
                      optionSelected={field.value as string | undefined}
                      defaultText={selectedSubtypeLabel}
                      defaultValue={field.value as string | undefined}
                      selectType="white"
                      error={!!fieldState.error}
                      required
                    />
                  </div>
                )}
              />
            ) : null}

            <div className={styles['grid-two-cols']}>
              <Controller
                control={form.control}
                name="eventDate"
                render={({ field, fieldState }) => (
                  <div className={styles['field-stack']}>
                    <Label
                      htmlFor="illiquid-event-date"
                      required
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="illiquid-asset-financial-events.fields.date"
                        ns="components"
                      />
                    </Label>
                    <DateInput
                      id="illiquid-event-date"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!fieldState.error}
                    />
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="currency"
                render={({ field, fieldState }) => (
                  <div className={styles['field-stack']}>
                    <Label
                      htmlFor="illiquid-event-currency"
                      required
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="illiquid-asset-financial-events.fields.currency"
                        ns="components"
                      />
                    </Label>
                    <OptionsSelector
                      optionsAvailable={currencyOptions}
                      onChangeValueSelected={val => field.onChange(val)}
                      optionSelected={field.value}
                      defaultText={defaultCurrency}
                      defaultValue={field.value}
                      selectType="white"
                      error={!!fieldState.error}
                      required
                    />
                  </div>
                )}
              />
            </div>

            <Controller
              control={form.control}
              name="amount"
              render={({ field, fieldState }) => {
                const showAmountError = !!fieldState.error && fieldState.isTouched;

                return (
                  <div className={styles['field-stack']}>
                    <Label
                      htmlFor="illiquid-event-amount"
                      required
                      error={showAmountError}
                    >
                      <Translation
                        i18nKey="illiquid-asset-financial-events.fields.amount"
                        ns="components"
                      />
                    </Label>
                    <NumericFormat
                      customInput={Input}
                      id="illiquid-event-amount"
                      value={field.value ?? ''}
                      onValueChange={({ floatValue }) => field.onChange(floatValue)}
                      onBlur={() => field.onBlur()}
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale={false}
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      thousandSeparator
                      isAllowed={({ floatValue }) =>
                        floatValue === undefined || floatValue > 0
                      }
                      className={classNames(
                        styles['numeric-input'],
                        showAmountError ? 'input-error' : undefined,
                      )}
                    />
                  </div>
                );
              }}
            />

            <Controller
              control={form.control}
              name="description"
              render={({ field }) => (
                <div className={styles['field-stack']}>
                  <Label htmlFor="illiquid-event-description">
                    <Translation
                      i18nKey="illiquid-asset-financial-events.fields.description"
                      ns="components"
                    />
                  </Label>
                  <Input
                    id="illiquid-event-description"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={t(
                      'illiquid-asset-financial-events.fields.description-placeholder',
                      { ns: 'components' },
                    )}
                    maxLength={100}
                  />
                </div>
              )}
            />

            <div className={styles['form-actions']}>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                className={styles['form-actions-button']}
                onClick={onCancelForm}
                disabled={isSaving}
                data-testid="illiquid-asset-financial-events-cancel"
              >
                <Translation i18nKey="common.cancel" ns="common" />
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="secondary"
                className={styles['form-actions-button']}
                disabled={!form.formState.isValid || isSaving}
                startIcon={
                  isSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                data-testid="illiquid-asset-financial-events-save"
              >
                <Translation i18nKey="common.save" ns="common" />
              </Button>
            </div>
          </form>
        </Collapse>

        {!hasEvents ? (
          <div
            className={styles['empty-state']}
            data-testid="illiquid-asset-financial-events-empty"
          >
            <span className={styles['empty-icon-wrap']}>
              <AppIcon icon="Calendar" color="disabled" size="lg" />
            </span>

            <Typography component="p" className={styles['event-title']}>
              <Translation
                i18nKey="illiquid-asset-financial-events.empty-title"
                ns="components"
              />
            </Typography>

            <Typography component="p" className={styles['event-meta']}>
              <Translation
                i18nKey="illiquid-asset-financial-events.empty-guidance"
                ns="components"
              />
            </Typography>
          </div>
        ) : (
          <div className={styles['year-group']}>
            {groupedEvents.map(group => (
              <section
                key={group.year}
                className={styles['year-group']}
                data-testid={`financial-events-year-${group.year}`}
              >
                <header className={styles['year-header']}>
                  <Typography component="span" className={styles['year-label']}>
                    {group.year}
                  </Typography>
                  <span className={styles['year-divider']} />
                </header>

                <ul className={styles['event-list']}>
                  {group.events.map(event => (
                    <EventRow
                      key={event.id}
                      event={event}
                      displayCurrency={displayCurrency}
                      deleteLabel={deleteLabel}
                      isDeleting={deletingEventId === event.id}
                      onDelete={onDeleteEvent}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </section>
    );
  },
});
